import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft, Loader2, BadgeCheck, CreditCard, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";

const TIERS = [
  {
    key: "connect",
    name: "Connect",
    tagline: "Start welcoming people well.",
    monthly: 50,
    annual: 500,
    premium: false,
    available: true,
    features: ["Welcome Kiosk", "Unlimited buttons & spotlights", "Connect Cards & Responses", "Custom 12×8 canvas layout", "Church branding & QR codes", "Email notifications", "Insights"],
  },
  {
    key: "grow",
    name: "Grow",
    tagline: "Build intentional pathways.",
    monthly: 100,
    annual: 1000,
    premium: true,
    available: false,
    features: ["Everything in Connect", "Automated follow-up", "Custom email responses", "Team management", "Advanced Insights", "Integrations & API access", "Ministry reports"],
  },
  {
    key: "multiply",
    name: "Multiply",
    tagline: "Lead with clarity.",
    monthly: 200,
    annual: 2000,
    premium: true,
    available: false,
    features: ["Everything in Grow", "Multi-campus support", "Executive dashboards", "Cross-platform analytics", "AI ministry insights", "Priority support", "Early access"],
  },
];

const TIER_RANK = { connect: 1, grow: 2, multiply: 3 };

export default function Billing() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tenant, setTenant] = useState(null);
  const [interval, setInterval] = useState("monthly");
  const [busy, setBusy] = useState("");
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/tenant/me"); setTenant(data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("canceled")) { toast.info("Checkout canceled — no charge made."); setParams({}, { replace: true }); return; }
    if (!sid) return;
    let attempts = 0;
    setPolling(true);
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        const done = data.status === "complete" || ["paid", "no_payment_required"].includes(data.payment_status);
        if (done) {
          setPolling(false);
          toast.success(`You're now on ${(data.tier || "").toUpperCase()}!`);
          await refresh(); await load();
          setParams({}, { replace: true });
          navigate("/admin", { replace: true }); // activated → continue into onboarding
          return;
        }
        if (data.status === "expired") { setPolling(false); toast.error("Checkout session expired."); setParams({}, { replace: true }); return; }
      } catch {}
      if (attempts >= 8) { setPolling(false); toast.message("Still processing — refresh in a moment."); return; }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async (tier) => {
    setBusy(tier);
    try {
      const { data } = await api.post("/billing/checkout", { tier, interval, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setBusy(""); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setBusy(""); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const currentTier = tenant?.tier || "connect";
  const status = tenant?.subscription_status;
  const blocked = tenant?.access_granted === false;
  const hasSubscription = !!tenant?.stripe_customer_id && ["active", "trialing", "past_due", "canceled"].includes(status);
  const trialUsed = tenant?.intro_trial_used;
  const periodEnd = tenant?.current_period_end ? new Date(tenant.current_period_end).toLocaleDateString() : null;
  const trialEnds = tenant?.intro_trial_ends_at ? new Date(tenant.intro_trial_ends_at).toLocaleDateString() : null;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="bg-white border-b border-[#E5E2DC] px-6 py-4 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2" data-testid="billing-back">
          <ArrowLeft className="w-4 h-4 text-[#5C5856]" />
          <Logo theme="black" height={26} />
        </Link>
        {tenant && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#5C5856]" data-testid="billing-current-plan">
              Current plan: <strong className="text-[#2A2626] uppercase">{currentTier}</strong>
              {status && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#F0EBE1]">{status}</span>}
            </span>
            {hasSubscription && (
              <Button variant="outline" size="sm" className="rounded-full" disabled={!!busy} onClick={openPortal} data-testid="billing-manage-subscription">
                <CreditCard className="w-4 h-4 mr-1" /> {busy === "portal" ? "Opening…" : "Manage subscription"}
              </Button>
            )}
          </div>
        )}
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {blocked && (
          <div className="mb-6 p-5 rounded-2xl bg-[#FBF0DA] border border-[#EAD9A8]" data-testid="billing-complete-subscription">
            <h2 className="text-lg font-semibold text-[#8A6D2F]">Complete your subscription</h2>
            <p className="text-sm text-[#8A6D2F]/90 mt-1">Your church account is saved, but MinistryHub stays locked until your subscription is active. Choose a plan below to finish and go live — your welcome screen, Connect Cards, and editor unlock right after checkout.</p>
          </div>
        )}
        <h1 className="font-serif-marketing text-4xl text-[#2A2626]">{blocked ? "Activate your church" : "Choose your plan"}</h1>
        <p className="text-[#5C5856] mt-2">Start Connect with a 14-day free trial. Grow &amp; Multiply are coming soon. Cancel anytime.</p>

        {(status === "trialing" && trialEnds) && (
          <div className="mt-4 p-3 rounded-xl bg-[#FBF0DA] border border-[#EAD9A8] text-sm text-[#8A6D2F] inline-flex items-center gap-2" data-testid="billing-trial-banner">
            <Sparkles className="w-4 h-4" /> Free trial active — ends {trialEnds}.
          </div>
        )}
        {(status === "active" && periodEnd) && (
          <div className="mt-4 text-sm text-[#5C5856]" data-testid="billing-renewal">Renews {periodEnd}.</div>
        )}

        {polling && (
          <div className="mt-6 p-4 rounded-2xl bg-white border border-[#E5E2DC] flex items-center gap-3" data-testid="billing-polling">
            <Loader2 className="w-5 h-5 animate-spin text-[#2A2626]" />
            <span className="text-[#2A2626]">Confirming your subscription…</span>
          </div>
        )}

        {/* Billing interval toggle */}
        <div className="mt-8 inline-flex items-center rounded-full bg-white border border-[#E5E2DC] p-1" data-testid="billing-interval-toggle">
          <button
            onClick={() => setInterval("monthly")}
            className={`px-5 h-9 rounded-full text-sm font-medium transition-colors ${interval === "monthly" ? "bg-[#2A2626] text-white" : "text-[#5C5856]"}`}
            data-testid="billing-interval-monthly"
          >Monthly</button>
          <button
            onClick={() => setInterval("annual")}
            className={`px-5 h-9 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${interval === "annual" ? "bg-[#2A2626] text-white" : "text-[#5C5856]"}`}
            data-testid="billing-interval-annual"
          >Annual <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#E7EFE6] text-[#4F6A4C]">Save 2 months</span></button>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-6">
          {TIERS.map((tier) => {
            const isCurrent = currentTier === tier.key && hasSubscription;
            const price = interval === "annual" ? tier.annual : tier.monthly;
            const trialEligible = tier.key === "connect" && !trialUsed;
            const highlight = tier.key === "connect";
            return (
              <div key={tier.key} className={`p-8 rounded-2xl bg-white relative flex flex-col ${highlight ? "border-2 border-[#2A2626] shadow-xl" : tier.available ? "border-2 border-[#E5E2DC]" : "border-2 border-dashed border-[#E5E2DC]"}`} data-testid={`billing-plan-${tier.key}`}>
                {tier.available && highlight && <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[#2A2626] text-white text-xs font-medium">Available now</div>}
                {!tier.available && <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[#F0EBE1] text-[#5C5856] border border-[#E5E2DC] text-xs font-medium" data-testid={`billing-comingsoon-badge-${tier.key}`}>Coming Soon</div>}
                <h3 className="text-2xl font-semibold">{tier.name}</h3>
                <p className="text-sm text-[#C05A46] mt-1">{tier.tagline}</p>
                <p className="mt-4">
                  <span className="text-5xl font-bold text-[#2A2626]">${price.toLocaleString()}</span>
                  <span className="text-[#5C5856]">/{interval === "annual" ? "yr" : "mo"}</span>
                </p>
                {tier.available && trialEligible && <p className="mt-1 text-xs text-[#4F6A4C] font-medium" data-testid={`billing-trial-note-${tier.key}`}>Includes 14-day free trial</p>}
                <ul className="mt-6 space-y-2 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#2A2626]"><Check className="w-4 h-4 text-[#2A2626] mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                {isCurrent ? (
                  <div className="mt-8 h-11 rounded-full bg-[#F0EBE1] text-[#5C5856] flex items-center justify-center gap-2 text-sm font-medium" data-testid={`billing-current-${tier.key}`}>
                    <BadgeCheck className="w-4 h-4 text-[#2A2626]" /> Current plan
                  </div>
                ) : !tier.available ? (
                  <div className="mt-8 h-11 rounded-full bg-[#F0EBE1] text-[#5C5856] flex items-center justify-center gap-2 text-sm font-medium border border-[#E5E2DC]" data-testid={`billing-comingsoon-${tier.key}`}>
                    Coming Soon
                  </div>
                ) : (
                  <Button onClick={() => checkout(tier.key)} disabled={!!busy} className={`mt-8 w-full h-11 rounded-full ${highlight ? "bg-[#2A2626] hover:bg-[#1A1717] text-white" : "border border-[#2A2626] bg-white text-[#2A2626] hover:bg-[#2A2626] hover:text-white"}`} data-testid={`billing-checkout-${tier.key}`}>
                    {busy === tier.key ? "Redirecting…" : trialEligible ? "Start 14-day free trial" : hasSubscription && TIER_RANK[tier.key] < TIER_RANK[currentTier] ? "Switch to " + tier.name : `Subscribe to ${tier.name}`}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-sm text-[#5C5856] flex items-center justify-center gap-1.5" data-testid="billing-stripe-trust">
          <Lock className="w-3.5 h-3.5" /> Payments are securely processed by Stripe. Cancel or change your plan anytime.
        </p>
      </main>
    </div>
  );
}
